import { useCallback, useState } from 'react';
import { useQuilttConnector, ConnectorSDKConnectorOptions, ConnectorSDKCallbackMetadata } from '@quiltt/react';
import { useQueryClient } from '@tanstack/react-query';
import { datadogRum } from '@datadog/browser-rum';

import { sendDatadogRumError } from 'utils/analytics/datadogRum';
import useEventTracking from 'modules/common/components/AnalyticsTracking/useEventTracking';
import QUILTT_ANALYTICS_EVENTS from 'services/quiltt/constants/analytics';
import { createUseBankStatementsAvailabilityListKey } from 'services/bookkeeping/queries/useBankStatementsAvailabilityList';
import { createUseHurdlrPlaidItemsKey } from 'services/hurdlr/queries/useHurdlrPlaidItems';

interface UseQuilttConnectProps {
    connectorId: string;
    connectionId?: string | null;
    institutionName: string;
    /**
     * Pre-fills the connector's institution search. Quiltt accepts an institution id or a free-text
     * search term; a term it cannot match leaves the member on an ordinary search. Ignored on repair,
     * where the connection already names its institution.
     */
    institutionSearch?: string;
    sourceSurface: string;
    onSuccess?: (metadata: ConnectorSDKCallbackMetadata) => void | Promise<void>;
    onAbort?: () => void;
    onError?: () => void;
}

export default function useQuilttConnect({
    connectorId,
    connectionId,
    institutionName,
    institutionSearch,
    sourceSurface,
    onSuccess,
    onAbort,
    onError,
}: UseQuilttConnectProps) {
    const [isConnecting, setIsConnecting] = useState(false);
    const queryClient = useQueryClient();
    const { sendTrackingEvent } = useEventTracking();

    const isRepair = !!connectionId;
    const flowType = isRepair ? 'repair' : 'connect';

    const baseTrackingData = {
        institution_name: institutionName,
        connector_id: connectorId,
        connection_id: connectionId,
        flow_type: flowType,
        source_surface: sourceSurface,
    };

    const baseDatadogData = {
        institutionName,
        connectorId,
        connectionId,
    };

    const quilttOptions: ConnectorSDKConnectorOptions = {
        connectionId: connectionId ?? undefined,
        institution: isRepair ? undefined : institutionSearch,
        onOpen: (metadata) => {
            datadogRum.addAction(isRepair ? 'Quiltt repair initiated' : 'Quiltt connector opened', {
                type: isRepair ? 'QUILTT_REPAIR_INITIATED' : 'QUILTT_CONNECTOR_OPENED',
                ...baseDatadogData,
                metadata,
            });
            sendTrackingEvent(
                isRepair ? QUILTT_ANALYTICS_EVENTS.REPAIR_CONNECTOR_OPENED : QUILTT_ANALYTICS_EVENTS.CONNECTOR_OPENED,
                baseTrackingData
            );
        },
        onExitSuccess: async (metadata) => {
            if (metadata?.connectionId) {
                datadogRum.addAction(isRepair ? 'Quiltt connection repaired' : 'Quiltt connector success', {
                    type: isRepair ? 'QUILTT_CONNECTION_REPAIRED' : 'QUILTT_CONNECTOR_SUCCESS',
                    ...baseDatadogData,
                    connectionId: metadata.connectionId,
                });
                sendTrackingEvent(
                    isRepair ? QUILTT_ANALYTICS_EVENTS.REPAIR_SUCCESS : QUILTT_ANALYTICS_EVENTS.CONNECTION_SUCCESS,
                    {
                        ...baseTrackingData,
                        connection_id: metadata.connectionId,
                    }
                );

                await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: createUseBankStatementsAvailabilityListKey(),
                        refetchType: 'all',
                    }),
                    queryClient.invalidateQueries({
                        queryKey: createUseHurdlrPlaidItemsKey(),
                        refetchType: 'all',
                    }),
                ]);

                await onSuccess?.(metadata);
            }
            setIsConnecting(false);
        },
        onExitAbort: (metadata) => {
            datadogRum.addAction(isRepair ? 'Quiltt repair aborted' : 'Quiltt connector aborted', {
                type: isRepair ? 'QUILTT_REPAIR_ABORTED' : 'QUILTT_CONNECTOR_ABORTED',
                ...baseDatadogData,
                metadata,
            });
            sendTrackingEvent(
                isRepair ? QUILTT_ANALYTICS_EVENTS.REPAIR_ABORTED : QUILTT_ANALYTICS_EVENTS.CONNECTION_ABORTED,
                baseTrackingData
            );
            setIsConnecting(false);
            onAbort?.();
        },
        onExitError: (metadata) => {
            sendDatadogRumError(
                isRepair ? 'Quiltt repair error' : 'Quiltt connector error',
                new Error(isRepair ? 'Quiltt repair error' : 'Quiltt connector error'),
                {
                    ...baseDatadogData,
                    metadata,
                }
            );
            sendTrackingEvent(
                isRepair ? QUILTT_ANALYTICS_EVENTS.REPAIR_FAILED : QUILTT_ANALYTICS_EVENTS.CONNECTION_FAILED,
                baseTrackingData
            );
            setIsConnecting(false);
            onError?.();
        },
    };

    const { open } = useQuilttConnector(connectorId, quilttOptions);

    const connect = useCallback(() => {
        if (isConnecting) {
            return;
        }
        setIsConnecting(true);
        open();
    }, [isConnecting, open]);

    return { open: connect, isConnecting };
}
